import { EASYRTC_URL } from '@/lib/config';

/**
 * One occupant of an EasyRTC room, as handed to `setRoomOccupantListener`.
 *
 * `username` is whatever the peer passed to `setUsername` — a display name
 * here. Anything else a peer wants to publish about itself rides in
 * `apiField`, which is how an Itsappy user id gets attached to an easyrtcid.
 */
export interface EasyRtcOccupant {
  username?: string;
  apiField?: Record<string, { fieldName: string; fieldValue: unknown }>;
}

/** The slice of the EasyRTC client global this app drives. */
export interface EasyRtc {
  setSocketUrl(url: string, options?: Record<string, unknown>): void;
  setCredential(credential: unknown): void;
  setUsername(name: string): boolean;
  enableAudio(enabled: boolean): void;
  enableVideo(enabled: boolean): void;
  enableAudioReceive(enabled: boolean): void;
  enableVideoReceive(enabled: boolean): void;
  setVideoDims(width: number, height: number, frameRate?: number): void;
  enableDataChannels(enabled: boolean): void;
  setAutoInitUserMedia(enabled: boolean): void;
  /** Pins capture to a specific device. Must be called before initMediaSource. */
  setAudioSource(deviceId: string | null): void;
  setVideoSource(deviceId: string | null): void;
  /** Builds the getUserMedia constraints. Overridable — see `withAudioProcessing`. */
  getUserMediaConstraints(): MediaStreamConstraints;
  initMediaSource(
    success: (stream: MediaStream) => void,
    failure: (code: string, text: string) => void,
    streamName?: string,
  ): void;
  register3rdPartyLocalMediaStream(stream: MediaStream, streamName: string): MediaStream | null;
  closeLocalMediaStream(streamName?: string): void;
  addStreamToCall(easyrtcId: string, streamName: string, receipt?: () => void): void;
  getPeerConnectionByUserId(easyrtcId: string): RTCPeerConnection | null;
  setPeerOpenListener(listener: (easyrtcId: string) => void): void;
  setRoomOccupantListener(
    listener: (
      roomName: string,
      occupants: Record<string, EasyRtcOccupant>,
      isPrimary: boolean,
    ) => void,
  ): void;
  setStreamAcceptor(
    acceptor: (easyrtcid: string, stream: MediaStream, streamName?: string) => void,
  ): void;
  setAcceptChecker(
    check: (
      easyrtcid: string,
      acceptor: (accepted: boolean, streamNames?: string[]) => void,
    ) => void,
  ): void;
  setOnStreamClosed(
    listener: (easyrtcid: string, stream: MediaStream, streamName?: string) => void,
  ): void;
  setDisconnectListener(listener: () => void): void;
  setOnError(listener: (error: { errorCode: string; errorText: string }) => void): void;
  setRoomApiField(roomName: string, fieldName?: string, fieldValue?: unknown): void;
  getRoomApiField(roomName: string, easyrtcid: string, fieldName: string): unknown;
  joinRoom(
    room: string,
    parameters: unknown,
    success?: (roomName: string) => void,
    failure?: (code: string, text: string, roomName: string) => void,
  ): void;
  leaveRoom(room: string, success?: () => void, failure?: () => void): void;
  connect(
    applicationName: string,
    success: (easyrtcid: string) => void,
    failure: (code: string, text: string) => void,
  ): void;
  disconnect(): void;
  /**
   * The live signalling socket, or null when there is none.
   *
   * Exposed because handing this singleton from one owner to the next has to
   * know when the previous owner has really let go: `connect` refuses while a
   * socket is still open, and refuses *silently*. See `awaitEasyRtcIdle`.
   */
  webSocket: unknown;
  /** True between `disconnect()` and the socket actually closing. */
  disconnecting: boolean;
  hangup(easyrtcid: string): void;
  hangupAll(): void;
  call(
    easyrtcid: string,
    success?: (easyrtcid: string, mediaType: string) => void,
    failure?: (code: string, text: string) => void,
    accepted?: (accepted: boolean, easyrtcid: string) => void,
    streamNames?: string[],
  ): void;
  getConnectStatus(easyrtcid: string): string;
  idToName(easyrtcid: string): string;
  NOT_CONNECTED: string;
  BECOMING_CONNECTED: string;
  IS_CONNECTED: string;
}

/**
 * Asks for echo cancellation, noise suppression and auto gain.
 *
 * EasyRTC builds its audio constraints as a bare `{}` (plus a device id, if
 * one was chosen), which requests none of these. Browsers happen to enable
 * them by default for microphone capture today, but that is a default rather
 * than a guarantee, and on a mesh call the cost of getting it wrong is a room
 * full of echo. Asking explicitly also documents the intent.
 *
 * `getUserMediaConstraints` is wrapped rather than replaced so the device
 * selection and video sizing EasyRTC computes are all preserved.
 */
export function withAudioProcessing(easyrtc: EasyRtc): void {
  const build = easyrtc.getUserMediaConstraints.bind(easyrtc);
  easyrtc.getUserMediaConstraints = () => {
    const constraints = build();
    if (constraints.audio && typeof constraints.audio === 'object') {
      constraints.audio = {
        ...constraints.audio,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
    }
    return constraints;
  };
}

/**
 * Keep a mesh screen share inside a predictable upload budget per peer.
 * Without this, Chromium may let every sender target several Mbps; on a
 * production connection the resulting queue is perceived as seconds of lag.
 */
export async function tuneScreenSender(
  easyrtc: EasyRtc,
  easyrtcId: string,
  track: MediaStreamTrack,
  peerCount: number,
): Promise<void> {
  const sender = easyrtc
    .getPeerConnectionByUserId(easyrtcId)
    ?.getSenders()
    .find((candidate) => candidate.track === track);
  if (!sender) return;

  const parameters = sender.getParameters();
  if (!parameters.encodings?.length) parameters.encodings = [{}];
  // A single 1080p presentation needs appreciably more than the camera-sized
  // budget used here previously or browser text turns into soft blocks. As a
  // mesh grows, divide a bounded total budget so latency cannot run away.
  const bitrate = Math.max(1_200_000, Math.min(3_500_000, Math.floor(5_000_000 / peerCount)));
  const sourceWidth = track.getSettings().width ?? 1920;
  for (const encoding of parameters.encodings) {
    encoding.maxBitrate = bitrate;
    encoding.maxFramerate = 12;
    encoding.scaleResolutionDownBy = Math.max(1, sourceWidth / 1920);
  }
  parameters.degradationPreference = 'maintain-resolution';
  await sender.setParameters(parameters).catch(() => undefined);
}

/**
 * Keep camera motion smooth without letting one mesh call saturate a user's
 * upload. Resolution is controlled on the capture track; this controls the
 * encoder budget independently for each peer connection.
 */
export async function tuneCameraSender(
  easyrtc: EasyRtc,
  easyrtcId: string,
  track: MediaStreamTrack,
  peerCount: number,
  presenting = false,
): Promise<void> {
  const sender = easyrtc
    .getPeerConnectionByUserId(easyrtcId)
    ?.getSenders()
    .find((candidate) => candidate.track === track);
  if (!sender) return;

  const parameters = sender.getParameters();
  if (!parameters.encodings?.length) parameters.encodings = [{}];
  const bitrate = presenting
    ? 280_000
    : peerCount <= 2
      ? 1_400_000
      : peerCount <= 4
        ? 850_000
        : 450_000;
  for (const encoding of parameters.encodings) {
    encoding.maxBitrate = bitrate;
    encoding.maxFramerate = presenting ? 10 : peerCount <= 2 ? 24 : peerCount <= 4 ? 18 : 12;
  }
  parameters.degradationPreference = 'balanced';
  await sender.setParameters(parameters).catch(() => undefined);
}

declare global {
  interface Window {
    easyrtc?: EasyRtc;
    io?: unknown;
  }
}

/** Loads a classic <script> once and resolves when it has executed. */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Fetches the EasyRTC client from the signalling service and returns the
 * global it installs.
 *
 * EasyRTC ships as a browser global rather than a module, and its client must
 * match the server version, so both it and its socket.io dependency are pulled
 * from the service at runtime instead of being bundled.
 *
 * That global is a singleton: only one part of the app may drive a connection
 * at a time. In practice the office and a meeting room are different routes,
 * so only one is ever mounted.
 */
export async function loadEasyRtc(): Promise<EasyRtc> {
  // socket.io must be a global before easyrtc.js evaluates.
  await loadScript(`${EASYRTC_URL}/easyrtc/socket.io/socket.io.js`);
  await loadScript(`${EASYRTC_URL}/easyrtc/easyrtc.js`);
  const easyrtc = window.easyrtc;
  if (!easyrtc) throw new Error('EasyRTC client did not load');
  return easyrtc;
}

/** EasyRTC rejects usernames outside a conservative identifier-ish charset. */
export function safeUsername(name: string): string {
  return name.replace(/[^\w .-]/g, '_').slice(0, 60) || 'guest';
}

/** How long a handoff waits for the previous owner to release the singleton. */
const HANDOFF_TIMEOUT_MS = 6000;
/** How often that wait re-checks. Short: the release is a 250ms timer. */
const HANDOFF_POLL_MS = 50;

/**
 * Waits until nothing else is driving the EasyRTC singleton.
 *
 * Two things make this necessary, and both of them show up the moment a
 * meeting is opened over a space, where the floor's proximity mesh and the
 * meeting's mesh are the same global object.
 *
 * `connect` bails out with only a console error when a socket is already open
 * — it calls neither the success nor the failure callback, so a caller that
 * waits to be told either way waits for ever. That is what left the meeting
 * panel sat on "Connecting…".
 *
 * And `disconnect` does not finish when it returns: it hangs up, then clears
 * the socket on a 250ms timer so any last message still reaches the server.
 * So even a floor that has already let go is, for a moment, indistinguishable
 * from one that has not — which is why this waits rather than checking once.
 */
export async function awaitEasyRtcIdle(easyrtc: EasyRtc): Promise<void> {
  const deadline = Date.now() + HANDOFF_TIMEOUT_MS;
  while (easyrtc.webSocket || easyrtc.disconnecting) {
    if (Date.now() >= deadline) {
      throw new Error('Another call is still closing — try joining again in a moment');
    }
    await new Promise((resolve) => setTimeout(resolve, HANDOFF_POLL_MS));
  }
}
